import type {ReactNode} from 'react';
import {ArrowDownLeft, ArrowUpRight, Forward, Lock, User, X} from 'lucide-react';
import {FlagImage} from 'react-international-phone';
import {DsAvatar, PhoneHQGlyph, StatusBadge} from 'ch3-ui-lib';
import type {Call} from '../../store/features/calls/callsService';
import {getFlag, getFormatedPhoneNumber} from '../../utilis/msisdnUtilis';
import type {CallDirection, CallRowView} from './callRowView';

// Small pieces shared by the call list and the call details.

export const lucide = 'shrink-0 !p-0 !text-current';

export const chip = 'inline-flex h-[26px] items-center gap-1.5 rounded-chip bg-surface-muted px-2.5 text-footnote font-medium text-ink';
export const whiteIconButton = 'inline-flex size-control-md cursor-pointer items-center justify-center rounded-control bg-surface text-ink hover:bg-line';

export const formatCallDate = (iso: string) => new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(iso));

/** A label / value row inside a details card. */
export const InfoRow = ({label, children}: { label: string; children: ReactNode }) => (
    <div className="grid min-h-11 grid-cols-[130px_1fr] items-center gap-2 border-0 border-t border-solid border-surface-muted px-4 py-2 first:border-t-0">
        <div className="text-footnote text-ink-muted">{label}</div>
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-callout text-ink">{children}</div>
    </div>
);

// Design: direction is a plain icon + label, no pill — arrows for in/out,
// a cross for missed, the forward arrow for forwarded.
const directionIcon: Record<CallDirection, typeof ArrowDownLeft> = {
    incoming: ArrowDownLeft,
    outgoing: ArrowUpRight,
    'missed-outgoing': ArrowUpRight,
    missed: X,
    forwarded: Forward,
};

const directionTone: Record<CallDirection, string> = {
    incoming: 'text-ink-muted-strong',
    outgoing: 'text-ink-muted',
    'missed-outgoing': 'text-danger-ink',
    missed: 'text-danger-ink',
    forwarded: 'text-ink-muted',
};

export const DirectionBadge = ({view}: { view: Pick<CallRowView, 'direction' | 'directionLabel'> }) => {
    const DirectionIcon = directionIcon[view.direction];
    return (
        <span
            data-testid="call-direction"
            className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-caption font-medium ${directionTone[view.direction]}`}
        >
            <DirectionIcon size={13} strokeWidth={2} aria-hidden="true" className={lucide}/>
            {view.directionLabel}
        </span>
    );
};

export const InternalBadge = () => (
    <StatusBadge tone="brand" icon={<Lock size={12} aria-hidden="true" className={lucide}/>}>Internal</StatusBadge>
);

export const SpamBadge = () => <StatusBadge tone="danger">Spam</StatusBadge>;

export const CallerAvatar = ({call, view, size = 32}: { call: Call; view: CallRowView; size?: 32 | 40 | 48 | 64 }) => {
    if (!view.known) {
        return <DsAvatar size={size} palette="neutral" icon={<User size={size >= 64 ? 28 : size === 32 ? 20 : 16} aria-hidden="true" className={lucide}/>}/>;
    }
    return <DsAvatar size={size} name={view.name} src={call.internalCallee?.avatarUrl || undefined}/>;
};

export const NumberFlag = ({msisdn, size = 16}: { msisdn: number; size?: number }) => (
    <FlagImage size={size} iso2={getFlag(msisdn) ?? 'us'} className="shrink-0 rounded-full"/>
);

/** 28px tile for the line a call came in on; internal calls get the brand tile. */
export const LineTile = ({call}: { call: Call }) => {
    if (call.internalCallee) {
        return (
            <span role="img" aria-label="Line: Internal" className="flex size-7 shrink-0 items-center justify-center rounded-chip bg-brand text-body-strong text-white">
                /
            </span>
        );
    }
    if (!call.exposedMsisdn) return null;
    const label = call.exposedMsisdn.label || getFormatedPhoneNumber(call.exposedMsisdn.msisdn, true);
    return (
        <span
            role="img"
            aria-label={`Line: ${label}`}
            title={label}
            className="flex size-7 shrink-0 items-center justify-center rounded-chip bg-surface text-ink-secondary shadow-[inset_0_0_0_1px_var(--line)]"
        >
            <PhoneHQGlyph name={call.exposedMsisdn.icon || 'call'} size={14}/>
        </span>
    );
};
